import type { Theme } from "../../../../types/theme";

type AdPlacementMap = {
  [key in AdPlacementContext]: {
    [key in Theme]: {
      [key in AdPlacements]?: { [key: string]: string };
    };
  };
};

type AdPlacementContext = "article" | "feed";

const validAdPlacementContexts: AdPlacementContext[] = ["article", "feed"];

type AdPlacements =
  | "afterByline"
  | "beforeLinks"
  | "afterLinks"
  | "widescreen1"
  | "widescreen2"
  | "topPanorama"
  | "sponsoredBanner"
  | "takeoverFront"
  | "takeoverRest"
  | "contentDisplay"
  | "insideNative"
  | "injectableDesktop"
  | "injectableMobile"
  | "backFillMobile"
  | "backFillDesktop";

const validAdPlacements: AdPlacements[] = [
  "afterByline",
  "beforeLinks",
  "afterLinks",
  "widescreen1",
  "widescreen2",
  "topPanorama",
  "sponsoredBanner",
  "takeoverFront",
  "takeoverRest",
  "contentDisplay",
  "insideNative",
  "injectableDesktop",
  "injectableMobile",
  "backFillMobile",
  "backFillDesktop",
];

interface ValidAdPlacement {
  placementContext: AdPlacementContext;
  placementWebName: AdPlacements;
  correct: true;
}
interface InvalidAdPlacement {
  placementContext: string;
  placementWebName: string;
  correct: false;
}

function parseAdPlacement(
  placement?: string,
): InvalidAdPlacement | ValidAdPlacement {
  if (typeof placement !== "string") {
    return { placementContext: "", placementWebName: "", correct: false };
  }

  const [placementContext, placementWebName] = placement.split(".");

  if (!placementWebName || !placementContext) {
    return { placementContext, placementWebName, correct: false };
  }

  if (
    validAdPlacements.includes(placementWebName as AdPlacements) &&
    validAdPlacementContexts.includes(placementContext as AdPlacementContext)
  ) {
    return {
      placementContext: placementContext as AdPlacementContext,
      placementWebName: placementWebName as AdPlacements,
      correct: true,
    };
  }

  return { placementContext, placementWebName, correct: false };
}

const adPlacementMap: AdPlacementMap = {
  article: {
    core: {
      afterByline: {
        mobile: "afterByline",
      },
      beforeLinks: {
        desktop: "articleModulOne",
        mobile: "mobileArticlePanorama",
      },
      afterLinks: {
        desktop: "dynamicDesktop",
        mobile: "dynamicMobileWeb",
      },
      widescreen1: {
        desktop: "articleWidescreen",
      },
      sponsoredBanner: {
        desktop: "stickyTop",
        mobile: "stickyTop",
      },
      insideNative: {
        desktop: "afterShareButtonsDesktop",
        mobile: "afterShareButtonsMobile",
      },
    },
    ekonomi: {
      beforeLinks: {
        desktop: "articleModulOne",
        mobile: "mobileArticlePanorama",
      },
      afterLinks: {
        desktop: "dynamicDesktop",
        mobile: "dynamicMobileWeb",
      },
      widescreen1: {
        desktop: "articleWidescreen",
      },
      sponsoredBanner: {
        desktop: "stickyTop",
        mobile: "stickyTop",
      },
      insideNative: {
        desktop: "afterShareButtonsDesktop",
        mobile: "afterShareButtonsMobile",
      },
    },
  },
  feed: {
    core: {
      widescreen1: {
        desktop: "widescreen1",
      },
      widescreen2: {
        desktop: "widescreen2",
      },
      topPanorama: {
        desktop: "topPanorama",
      },
      takeoverFront: {
        desktop: "takeoverFrontDesktop",
        mobile: "takeoverFrontMobile",
      },
      takeoverRest: {
        desktop: "takeoverRestDesktop",
        mobile: "takeoverRestMobile",
      },
      sponsoredBanner: {
        desktop: "stickyTop",
        mobile: "stickyTop",
      },
      injectableDesktop: {
        desktop: "injectableDesktop",
      },
      injectableMobile: {
        mobile: "injectableMobile",
      },
      backFillDesktop: {
        desktop: "backFillDesktop",
      },
      backFillMobile: {
        mobile: "backFillMobile",
      },
    },
    ekonomi: {
      widescreen1: {
        desktop: "widescreen1",
      },
      widescreen2: {
        desktop: "widescreen2",
      },
      topPanorama: {
        desktop: "topPanorama",
      },
      takeoverFront: {
        desktop: "takeoverFrontDesktop",
        mobile: "takeoverFrontMobile",
      },
      sponsoredBanner: {
        desktop: "stickyTop",
        mobile: "stickyTop",
      },
      injectableDesktop: {
        desktop: "injectableDesktop",
      },
      injectableMobile: {
        mobile: "injectableMobile",
      },
      backFillDesktop: {
        desktop: "backFillDesktop",
      },
      backFillMobile: {
        mobile: "backFillMobile",
      },
    },
  },
};

export { adPlacementMap, parseAdPlacement };
export type { AdPlacementContext };
